/**
 * Generate a CSS selector for an element
 */
export function getElementSelector(element: HTMLElement): string {
    const parts: string[] = [];
    let current: HTMLElement | null = element;
    let depth = 0;
    const maxDepth = 5;

    while (current && depth < maxDepth) {
        let selector = current.tagName.toLowerCase();

        if (current.id) {
            selector += `#${current.id}`;
            parts.unshift(selector);
            break; // ID is unique, stop here
        }

        if (current.className) {
            const classes = current.className
                .split(/\s+/)
                .filter((c) => c && !c.startsWith('honch'))
                .slice(0, 3)
                .join('.');
            if (classes) {
                selector += `.${classes}`;
            }
        }

        // Add nth-child if needed for uniqueness
        if (current.parentElement) {
            const siblings = Array.from(current.parentElement.children);
            const sameTagSiblings = siblings.filter((s) => s.tagName === current!.tagName);
            if (sameTagSiblings.length > 1) {
                const index = sameTagSiblings.indexOf(current) + 1;
                selector += `:nth-of-type(${index})`;
            }
        }

        parts.unshift(selector);
        current = current.parentElement;
        depth++;
    }

    return parts.join(' > ');
}

/**
 * Get readable text from an element
 */
export function getElementText(element: HTMLElement): string | undefined {
    // Try aria-label first
    const ariaLabel = element.getAttribute('aria-label');
    if (ariaLabel) return truncate(ariaLabel, 100);

    // Try title
    if (element.title) return truncate(element.title, 100);

    // Try innerText (skip for input elements)
    const tagName = element.tagName.toLowerCase();
    if (tagName !== 'input' && tagName !== 'select' && tagName !== 'textarea') {
        const text = element.innerText?.trim();
        if (text) return truncate(text, 100);
    }

    // For inputs, try placeholder
    if (tagName === 'input') {
        const placeholder = (element as HTMLInputElement).placeholder;
        if (placeholder) return truncate(placeholder, 100);
    }

    return undefined;
}

/**
 * Check if an element should be ignored based on selectors
 */
export function shouldIgnoreElement(element: HTMLElement, excludeSelectors: string[]): boolean {
    for (const selector of excludeSelectors) {
        try {
            if (element.matches(selector)) return true;
            if (element.closest(selector)) return true;
        } catch (_e) {
            // Invalid selector, skip
        }
    }
    return false;
}

function truncate(str: string, maxLength: number): string {
    if (str.length <= maxLength) return str;
    return str.substring(0, maxLength - 3) + '...';
}
